import { useCallback, useState } from "react";
import { scanForPii, MATCH_LOGGABLE_TYPES } from "../utils/piiDetection";
import PiiDetectionService from "../services/PiiDetectionService";
import { useCampaign } from "../context/campaign";

const buildHitKey = (type, value, occurrenceIndex) => `${type}::${value}::${occurrenceIndex}`;

const buildMatches = hits =>
  hits.filter(hit => MATCH_LOGGABLE_TYPES.includes(hit.type)).map(hit => ({ type: hit.type, value: hit.value }));

// Assigns each hit a stable key (type + value + occurrence index) so a
// specific occurrence can be resolved independently, and so re-scanning the
// same text on a later blur can recognize it as the same occurrence.
const keyHits = hits => {
  const seenCounts = {};
  return hits.map(hit => {
    const dedupeKey = `${hit.type}::${hit.value}`;
    const occurrenceIndex = seenCounts[dedupeKey] || 0;
    seenCounts[dedupeKey] = occurrenceIndex + 1;
    return { ...hit, key: buildHitKey(hit.type, hit.value, occurrenceIndex) };
  });
};

/**
 * Tracks per-field, per-occurrence PII detection state for a coaching
 * form/stage and gates progression until every flagged occurrence is
 * resolved or overridden.
 */
const usePiiGuard = (
  sessionId,
  stage,
  coacheeName,
  coachName,
  campaigns = [],
  clientCompanyNames = [],
  subFunctionalUnitNames = []
) => {
  const [fieldState, setFieldState] = useState({});
  // AI Coaching pilot region gate: the PII guard exists to keep sensitive
  // data out of the AI pipeline, so outside the pilot it has nothing to
  // protect - checkField becomes a no-op (no scan, no state, no log) rather
  // than just hiding the resulting UI. Mirrors the isAiCoachingEnabled check
  // already centralized in useNudgePopover/AiInsightDiv. See
  // coach-2125-pii-region-gate.md.
  const { isAiCoachingEnabled } = useCampaign();

  const logFieldFlag = useCallback(
    (fieldReference, detectionTypes, matches) => {
      PiiDetectionService.logPiiFlag({
        sessionId,
        stage,
        fieldReference,
        detectionTypes,
        matches,
      });
    },
    [sessionId, stage]
  );

  const checkField = useCallback(
    (fieldKey, value) => {
      if (!isAiCoachingEnabled) return [];

      const rawHits = scanForPii(value, {
        coacheeName,
        coachName,
        campaigns,
        clientCompanyNames,
        subFunctionalUnitNames,
      });
      const keyedHits = keyHits(rawHits);
      const prevHits = fieldState[fieldKey]?.hits || [];
      const prevResolvedKeys = new Set(prevHits.filter(hit => hit.resolved).map(hit => hit.key));
      const hits = keyedHits.map(hit => ({ ...hit, resolved: prevResolvedKeys.has(hit.key) }));

      setFieldState(prev => ({ ...prev, [fieldKey]: { hits } }));

      if (rawHits.length > 0) {
        logFieldFlag(fieldKey, rawHits.map(hit => hit.type), buildMatches(rawHits));
      }

      return hits.filter(hit => !hit.resolved);
    },
    [
      isAiCoachingEnabled,
      coacheeName,
      coachName,
      campaigns,
      clientCompanyNames,
      subFunctionalUnitNames,
      fieldState,
      logFieldFlag,
    ]
  );

  const overrideHit = useCallback(
    (fieldKey, hit, reason) => {
      setFieldState(prev => {
        const current = prev[fieldKey];
        if (!current) return prev;
        return {
          ...prev,
          [fieldKey]: {
            hits: current.hits.map(h => (h.key === hit.key ? { ...h, resolved: true } : h)),
          },
        };
      });
      PiiDetectionService.logPiiFlag({
        sessionId,
        stage,
        fieldReference: fieldKey,
        detectionTypes: [hit.type],
        overridden: true,
        overrideReason: reason,
        matches: buildMatches([hit]),
      });
    },
    [sessionId, stage]
  );

  // Any unresolved hit blocks, regardless of confidence tier - the PII
  // detection ticket's AC requires the user not be allowed to proceed until
  // all detected sensitive/PII data is removed, with no carve-out for
  // lower-confidence detections (bank-account heuristic, passport, heuristic
  // name).
  const isBlocked = useCallback(
    fieldKey => (fieldState[fieldKey]?.hits || []).some(hit => !hit.resolved),
    [fieldState]
  );

  const getHits = useCallback(
    fieldKey => (fieldState[fieldKey]?.hits || []).filter(hit => !hit.resolved),
    [fieldState]
  );

  const hasAnyBlocking = Object.keys(fieldState).some(fieldKey =>
    fieldState[fieldKey].hits.some(hit => !hit.resolved)
  );

  return {
    checkField,
    overrideHit,
    isBlocked,
    getHits,
    hasAnyBlocking,
  };
};

export default usePiiGuard;
